import {
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  fetchSignInMethodsForEmail,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  linkWithPopup,
  OAuthProvider,
  sendEmailVerification,
  signInWithPopup,
  signInWithRedirect,
  updateProfile
} from "firebase/auth"
import { FirebaseTokenLoader, JobsService } from "@telarya/telarya-sdk"

import generateEndpoint from "@/endpoint"
import { i18n } from "@/i18n"
import { createAuthenticatedHttpClient } from "@/services/httpClient"
import reportError from "@/utils/reportError"

const SOCIAL_PROVIDER_KINDS = ["google", "facebook", "apple"]

export class FirebaseSignupService {
  constructor() {
    this.jobsServiceClient = null
  }

  getAuth() {
    return getAuth()
  }

  getJobsServiceClient(providedClient = null) {
    if (providedClient) return providedClient
    if (this.jobsServiceClient) return this.jobsServiceClient

    const httpClient = createAuthenticatedHttpClient(new FirebaseTokenLoader(this.getAuth()))
    this.jobsServiceClient = new JobsService(generateEndpoint(), httpClient)
    return this.jobsServiceClient
  }

  resolveLanguageCode(languageCode) {
    if (languageCode) return String(languageCode)

    const rawLocale = i18n?.global?.locale
    if (typeof rawLocale === "string") return rawLocale
    if (rawLocale && typeof rawLocale === "object" && typeof rawLocale.value === "string") return rawLocale.value

    return "en"
  }

  syncAuthLanguage(auth, languageCode) {
    auth.languageCode = this.resolveLanguageCode(languageCode)
  }

  createSocialProvider(kind) {
    const providerKind = String(kind || "").toLowerCase()

    if (providerKind === "google") {
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({ prompt: "select_account" })
      return provider
    }

    if (providerKind === "facebook") {
      const provider = new FacebookAuthProvider()
      provider.addScope("email")
      return provider
    }

    if (providerKind === "apple") {
      const provider = new OAuthProvider("apple.com")
      provider.addScope("email")
      provider.addScope("name")
      return provider
    }

    throw new Error(`Unsupported social provider: ${kind}`)
  }

  resolveSocialProviderId(kind) {
    const providerKind = String(kind || "").toLowerCase()
    if (providerKind === "google") return "google.com"
    if (providerKind === "facebook") return "facebook.com"
    if (providerKind === "apple") return "apple.com"
    return ""
  }

  canLinkSocialProvider(kind, user = this.getAuth().currentUser) {
    if (!user) return false
    const providerId = this.resolveSocialProviderId(kind)
    if (!providerId) return false
    const alreadyLinked = Array.isArray(user.providerData)
      ? user.providerData.some((provider) => provider?.providerId === providerId)
      : false
    return !alreadyLinked
  }

  async registerFirebaseUser(providedJobsServiceClient = null) {
    const jobsServiceClient = this.getJobsServiceClient(providedJobsServiceClient)
    try {
      return await jobsServiceClient.registerFirebaseUser()
    } catch (error) {
      reportError(error, { context: "FirebaseSignupService.registerFirebaseUser" })
      throw error
    }
  }

  async signUpWithEmail({ email, password, displayName, sendVerification = false, languageCode, jobsServiceClient } = {}) {
    const auth = this.getAuth()
    this.syncAuthLanguage(auth, languageCode)

    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password)

      if (displayName && credential?.user) {
        await updateProfile(credential.user, { displayName })
      }

      if (sendVerification && credential?.user) {
        await sendEmailVerification(credential.user)
      }

      await this.registerFirebaseUser(jobsServiceClient)

      return credential
    } catch (error) {
      reportError(error, { context: "FirebaseSignupService.signUpWithEmail" })
      throw error
    }
  }

  async signUpWithSocialPopup(kind, { languageCode, jobsServiceClient } = {}) {
    const auth = this.getAuth()
    this.syncAuthLanguage(auth, languageCode)

    try {
      const provider = this.createSocialProvider(kind)
      const credential = await signInWithPopup(auth, provider)
      await this.registerFirebaseUser(jobsServiceClient)
      return credential
    } catch (error) {
      reportError(error, { context: "FirebaseSignupService.signUpWithSocialPopup", provider: kind })
      throw error
    }
  }

  async linkSocialProviderPopup(kind, { languageCode } = {}) {
    const auth = this.getAuth()
    this.syncAuthLanguage(auth, languageCode)
    const user = auth.currentUser
    if (!user) {
      throw new Error("No authenticated user available for provider linking.")
    }
    if (!this.canLinkSocialProvider(kind, user)) {
      return null
    }

    try {
      const provider = this.createSocialProvider(kind)
      return await linkWithPopup(user, provider)
    } catch (error) {
      reportError(error, { context: "FirebaseSignupService.linkSocialProviderPopup", provider: kind })
      throw error
    }
  }

  async signUpWithSocialRedirect(kind, { languageCode } = {}) {
    const auth = this.getAuth()
    this.syncAuthLanguage(auth, languageCode)

    try {
      const provider = this.createSocialProvider(kind)
      await signInWithRedirect(auth, provider)
    } catch (error) {
      reportError(error, { context: "FirebaseSignupService.signUpWithSocialRedirect", provider: kind })
      throw error
    }
  }

  async getSocialRedirectResult({ jobsServiceClient } = {}) {
    const auth = this.getAuth()
    try {
      const credential = await getRedirectResult(auth)
      if (credential?.user) {
        await this.registerFirebaseUser(jobsServiceClient)
      }
      return credential
    } catch (error) {
      reportError(error, { context: "FirebaseSignupService.getSocialRedirectResult" })
      throw error
    }
  }

  async getSignInMethodsForEmail(email) {
    const auth = this.getAuth()
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email)
      return Array.isArray(methods) ? methods : []
    } catch (error) {
      reportError(error, { context: "FirebaseSignupService.getSignInMethodsForEmail" })
      throw error
    }
  }

  getSocialProviderKinds() {
    return [...SOCIAL_PROVIDER_KINDS]
  }
}

export default new FirebaseSignupService()
